import { Button, Container, DatedList, Icon } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import prepareFilterParams from 'utils/prepareFilterParams'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import formatRelative from 'utils/dateFns/formatRelative'
import EmptyResults from 'components/EmptyResults'
import FilterPanel from 'components/Filters/FilterPanel'
import GridSpinner from 'components/UI/GridSpinner'
import Item from 'modules/Services/Account/subcomponents/TransactionsList/subcomponents/TransactionRow'
import styles from 'modules/Services/Account/subcomponents/TransactionsList/transactions-list.module.scss'
import accountStyles from 'modules/Services/Account/network-account.module.scss'
import config from 'modules/Services/Account/config/filters'
import { useGetAccountAction } from 'modules/Services/hooks/useGetAccountAction'
import {
  ACCOUNT_TRANSFERS_CLEAN,
  FETCH_ACCOUNT_TRANSFERS,
  SET_ACCOUNT_TRANSFERS_FILTERS,
} from 'modules/Services/types'

const TransactionList = ({ networkId, accountId, networkType }) => {
  const { t } = useTranslation(['services'])
  const dispatch = useDispatch()
  const { cancelableDispatch } = useCancelToken()
  const pathGen = useGeneratePathI18n()
  const [page, setPage] = useState(1)

  const [accountTransfersConfig, setAccountTransfersConfig] =
    useLocalStorage('accountTransfersConfig')

  const {
    filters,
    main_action,
    transfers: { loading, loaded, failed, data = [], pagination = {} },
    i18n,
  } = useSelector(
    (state) => ({
      filters: state['services/network-account'].filters,
      main_action: state['services/network-account'].main_action,
      transfers: state['services/network-account'].transfers,
      i18n: state['app'].i18n,
    }),
    shallowEqual,
  )

  const filterManager = useFiltersManager({
    filters,
    config: config(),
    setAction: SET_ACCOUNT_TRANSFERS_FILTERS,
    additionalFilterState: accountTransfersConfig?.[networkType],
    localStorageKey: 'services/network-account',
  })

  useEffect(
    () =>
      setAccountTransfersConfig({
        ...accountTransfersConfig,
        [networkType]: { ...filterManager.filters },
      }),
    [filterManager.filters, networkType],
  )

  const depositConfig = useGetAccountAction('deposit')

  const {
    query = '',
    status = [],
    date_from = '',
    date_to = '',
  } = useMemo(() => filters || {}, [filters])

  useEffect(() => {
    dispatch({ type: ACCOUNT_TRANSFERS_CLEAN })
    setPage(1)
  }, [filters])

  useEffect(() => {
    !!networkId &&
      cancelableDispatch({
        type: FETCH_ACCOUNT_TRANSFERS,
        payload: {
          networkId,
          accountId,
          params: prepareFilterParams({
            query: query,
            offset: (page - 1) * pagination.limit,
            limit: pagination.limit,
            status: status,
            date_from: date_from,
            date_to: date_to,
          }),
        },
      })
  }, [filters, networkId, page])

  const transactions = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        date: item.created_at * 1000,
      })),
    [data],
  )

  useEffect(() => {
    return () => dispatch({ type: ACCOUNT_TRANSFERS_CLEAN })
  }, [])

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(filterManager.params, data, loaded)

  if (firstLoad) {
    return <GridSpinner width={32} />
  }

  if (totallyEmptyList && !loading) {
    return (
      <div className={'layout-tile'}>
        <EmptyResults
          className={styles['transfers__empty-list']}
          icon={<Icon name={'columns'} />}
          header={t('transfers_list.empty_list_header')}
          text={t('transfers_list.empty_list_label')}
          actionButton={
            main_action === 'deposit' ? (
              <Button
                startIcon={<Icon name={'card'} />}
                className={styles['transfers__empty-list-button']}
                size="l"
                rounded
                variant="primary"
                type="button"
                component={Link}
                to={pathGen(depositConfig.url, {
                  i18n,
                  accountId,
                })}
              >
                {t('button.fund')}
              </Button>
            ) : null
          }
        />
      </div>
    )
  }

  return (
    <Container
      className={clsx(accountStyles['network-account__transfers-container'], 'layout-tile')}
    >
      <FilterPanel
        filterManager={filterManager}
        className={accountStyles['network-account__filter-panel']}
      />
      <DatedList
        emptyComponent={
          <EmptyResults
            className={styles['transfers__empty-list']}
            icon={<Icon name={'columns'} />}
          />
        }
        itemComponent={Item}
        pagination={pagination}
        className={styles['transfers__list']}
        items={transactions}
        moreButtonLabel={t('wallets::transaction.showMore')}
        handleGetMoreItems={() => setPage(page + 1)}
        loading={loading}
        loaded={loaded}
        loadFailed={failed}
        l10n={{
          formatFunction: formatRelative,
          today: t('common::dated_list.date_format.today'),
          yesterday: t('common::dated_list.date_format.yesterday'),
          formatDates: {
            short: t('common::dated_list.date_format.short'),
            long: t('common::dated_list.date_format.long'),
          },
        }}
      />
    </Container>
  )
}

TransactionList.propTypes = {
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  accountId: PropTypes.string,
}

export default TransactionList
